import React, {Component} from "react"
import {connect} from "react-redux"
import {get} from "lodash"

import idHelper from "@irati/utils/id"
import authentication from "@irati/auth"
import TenantHomePageQuery from "@irati/culture-hub/services/apollo/queries/TenantHomePage"
import {ReduxState} from "@irati/store/types"
import Loading from "@irati/ui/src/Loading"
import PerkboxTour, {TourStep} from "@irati-web/tour"
import OnboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import {CompanyNewsTile, CompanyNewsTileType, TenantOnboarding, UserOnboarding, FlexiPointsExpiry} from "@irati/types"
import {hangPromise} from "@irati/utils/promise"
import "@irati/deals/services/favourites"
import homepageTracking from "@irati-tracking/homepage"
import cultureHubTracking from "@irati-tracking/homepage/cultureHub"
import {RouteComponentProps, withRouter} from "react-router-dom"

import homepageTourSteps from "../tour"
import {HomePageView} from "../components/types"
import CompanyNews from "../components/CompanyNews"
import TileDetailsModal from "../components/CompanyNews/TileDetailsModal"
import {CultureHubProvider} from "../stores/hooks"

interface State {
  tourSteps: TourStep[]
  loadOnboardingData: Promise<any>
  tenantEligibleForTour: boolean
  userEligibleForTour: boolean
  pageView: HomePageView
  expiredFlexiPoints: FlexiPointsExpiry | null
  isEditView: boolean
  isCHEditing: boolean | undefined
}

interface Props extends RouteComponentProps {
  isMobile: boolean
}

class CultureHub extends Component<Props, State> {
  state: State = {
    pageView: this.props.isMobile ? HomePageView.EMPLOYEE_VIEW : HomePageView.EDIT_CONTENT_VIEW,
    tourSteps: homepageTourSteps,
    tenantEligibleForTour: false,
    userEligibleForTour: false,
    loadOnboardingData: hangPromise(),
    expiredFlexiPoints: null,
    isCHEditing: undefined
  }

  toggleEditView = () => {
    this.setState({
      isCHEditing: !this.state.isCHEditing
    })
  }

  trackedViewCultureHub = false

  componentDidMount() {
    const {uuid = ""} = authentication.user()

    const loadTenantOnboarding = OnboardingService.getTenantOnboarding().then((tenantOnboarding: TenantOnboarding) => {
      if (!tenantOnboarding || !tenantOnboarding?.enabled) {
        this.setState({
          tenantEligibleForTour: true
        })
      }
    })
    const promises = [loadTenantOnboarding]

    if (!authentication.isUserGhost()) {
      const loadUserOnboarding = OnboardingService.get(uuid).then(({completedSteps}: UserOnboarding) => {
        if (!completedSteps.includes(OnboardingStep.HAS_COMPLETED_HOMEPAGE_TOUR)) {
          this.setState({
            userEligibleForTour: true
          })
        }
      })
      promises.push(loadUserOnboarding)
    }

    this.setState({
      loadOnboardingData: Promise.all(promises)
    })
  }

  handleCompleteTour = (): void => {
    OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_COMPLETED_HOMEPAGE_TOUR)
  }

  handleCultureHubTracking = (count: number): void => {
    if (!this.trackedViewCultureHub) {
      this.trackedViewCultureHub = true
      homepageTracking.homepageView({culture_hub_count: count})
      cultureHubTracking.cultureHubClick(count)
    }
  }

  getNumberOfNotEmptyTiles = (companyNews: CompanyNewsTile[] | undefined) => {
    return companyNews?.filter((c) => c.config?.type !== CompanyNewsTileType.Empty).length || 0
  }

  render() {
    const {tourSteps, loadOnboardingData, tenantEligibleForTour, userEligibleForTour, isCHEditing} = this.state

    const canEdit = authentication.apps("platform.cultureHub.update")

    const getTenantHomePageVars = {
      tenantId: authentication.userTenant().uuid
    }

    const idNamespace = idHelper("HomePage", {})
    const pageView = isCHEditing ? HomePageView.EDIT_CONTENT_VIEW : HomePageView.EMPLOYEE_VIEW

    return (
      <TenantHomePageQuery variables={getTenantHomePageVars}>
        {({data}) => {
          const tile = data?.companyNews.find((t) => t.uuid === this.props.match.params.id)

          this.handleCultureHubTracking(this.getNumberOfNotEmptyTiles(data?.companyNews))
          return (
            <Loading load={loadOnboardingData}>
              <PerkboxTour
                run={tenantEligibleForTour && userEligibleForTour && canEdit}
                steps={tourSteps}
                onComplete={this.handleCompleteTour}
              />
              <CultureHubProvider>
                <CompanyNews
                  id={idNamespace}
                  pageView={pageView}
                  companyNews={data?.companyNews || []}
                  toggleEditView={this.toggleEditView}
                />
              </CultureHubProvider>

              <TileDetailsModal tile={tile} tilesCount={data?.companyNews.length} />
            </Loading>
          )
        }}
      </TenantHomePageQuery>
    )
  }
}

const mapStateToProps = ({mq}: ReduxState) => {
  return {
    isMobile: get(mq, "lessThan.medium", false)
  }
}

export default connect(mapStateToProps)(withRouter(CultureHub))
